import React, { useState, useEffect, useCallback, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useLocation, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  Brain,
  Info,
  XCircle,
  CheckCircle,
  Clock,
  Trophy,
  HelpCircle,
  BarChart2,
} from "lucide-react";
import { useAuth } from "../../hooks/AuthenticationProvider";
import PvPLobby from "../../components/PvPLobby";
import MultiplayerGame from "../../components/MultiplayerGame";

const MemoryMatrixMultiWrapper = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [showLobby, setShowLobby] = useState(true);
  const [activeMatch, setActiveMatch] = useState(null);

  useEffect(() => {
    // Check if we have a match ID in URL
    const searchParams = new URLSearchParams(location.search);
    const matchId = searchParams.get("matchId");

    if (matchId) {
      setActiveMatch(matchId);
      setShowLobby(false);
    }
  }, [location]);

  const handleMatchCreated = (matchId) => {
    setActiveMatch(matchId);
    setShowLobby(false);
  };

  return (
    <div className="h-full">
      {showLobby ? (
        <div className="bg-slate-800 border border-slate-700 rounded-xl p-6">
          <PvPLobby
            game="memory-matrix"
            onMatchCreated={handleMatchCreated}
            onLeaveLobby={() => navigate("/gameroom")}
            gamesData={[{ id: "memory-matrix", name: "Memory Matrix" }]}
          />
        </div>
      ) : (
        <MultiplayerGame
          matchId={activeMatch}
          gameComponent={MemoryMatrixMulti} // This is your existing component
          onExit={() => navigate("/gameroom")}
        />
      )}
    </div>
  );
};

const MemoryMatrixMulti = ({
  gameState,
  updateGameState,
  submitScore,
  opponent,
}) => {
  const { user } = useAuth();

  // --- Game States ---
  // For multiplayer we default to play mode; however, we show a ready-to-play overlay before the game starts.
  const [gameMode, setGameMode] = useState("play");
  const [gameStarted, setGameStarted] = useState(false);
  const [waitingForOpponent, setWaitingForOpponent] = useState(false);

  // Gameplay metrics
  const [score, setScore] = useState(0);
  const [correctResponses, setCorrectResponses] = useState(0);
  const [incorrectResponses, setIncorrectResponses] = useState(0);
  const [misses, setMisses] = useState(0);
  const [reactionTimes, setReactionTimes] = useState([]);
  const [gameHistory, setGameHistory] = useState([]); // Record user responses per pattern

  // Sequence & grid state
  const [currentSequence, setCurrentSequence] = useState([]);
  const [currentPosition, setCurrentPosition] = useState(0);
  const [activeSquare, setActiveSquare] = useState(null);

  // Timer settings
  const GAME_DURATION = 180; // seconds (3 minutes)
  const [timeLeft, setTimeLeft] = useState(GAME_DURATION);
  const timerRef = useRef(null);
  const isPlayingRef = useRef(false);

  // Countdown for multiplayer start
  const [showCountdown, setShowCountdown] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const countdownRef = useRef(null);

  // For Memory Matrix specifics: fixed level (n‑back) and grid settings
  const [level] = useState(2); // Multiplayer uses a standard 2‑Back mode
  const matrixSize = 3;
  const numSquares = matrixSize * matrixSize;
  const patternCount = 30;
  const baseSpeed = 2000; // ms between patterns
  const basePatternVisibility = 1200; // ms pattern is visible

  // Ref to capture the time of pattern display for reaction timing
  const lastInteractionTime = useRef(Date.now());

  // --- Effects & Multiplayer Synchronization ---

  // Keep the isPlaying ref updated with gameStarted
  useEffect(() => {
    isPlayingRef.current = gameStarted;
  }, [gameStarted]);

  // When both players are ready, update the game state (multiplayer synchronization)
  useEffect(() => {
    if (!gameState || !opponent || !user?.email) return;
    const playerReadyState = gameState.playerReady || {};
    const allPlayersReady =
      playerReadyState[user.email] && playerReadyState[opponent.email];
    if (allPlayersReady && gameState.status === "pending" && updateGameState) {
      updateGameState({ status: "ready" });
    }
  }, [gameState, opponent, user?.email, updateGameState]);

  // Listen to gameState changes (e.g., ready, active, completed)
  useEffect(() => {
    if (gameState && !gameStarted && !showCountdown) {
      console.log("Game state update received:", gameState.status);
      if (gameState.status === "ready" || gameState.status === "active") {
        console.log("Starting countdown...");
        startGame();
      } else if (gameState.status === "completed") {
        if (isPlayingRef.current) {
          endGame();
        }
      }
    }
  }, [gameState, gameStarted, showCountdown]);

  // If opponent quits, end the game automatically
  useEffect(() => {
    if (gameState?.playerQuit && gameState.playerQuit !== user?.email) {
      if (isPlayingRef.current) {
        endGame();
        alert("Your opponent has left the game. You win by forfeit!");
      }
    }
  }, [gameState?.playerQuit, user?.email]);

  // Clear timers on unmount
  useEffect(() => {
    return () => {
      clearInterval(timerRef.current);
      clearInterval(countdownRef.current);
    };
  }, []);

  // --- Multiplayer Ready & Countdown ---

  const handleReadyClick = () => {
    setWaitingForOpponent(true);
    if (updateGameState) {
      updateGameState({
        playerReady: {
          ...(gameState?.playerReady || {}),
          [user.email]: true,
        },
        // Optionally include device information
        playerInfo: {
          ...(gameState?.playerInfo || {}),
          [user.email]: {
            device: navigator.userAgent,
            connectionTime: new Date().toISOString(),
            screenWidth: window.innerWidth,
            screenHeight: window.innerHeight,
          },
        },
      });
    }
  };

  const startGame = () => {
    console.log("Countdown started");
    setShowCountdown(true);
    setCountdown(3);
    countdownRef.current = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(countdownRef.current);
          startGameAfterCountdown();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const startGameAfterCountdown = () => {
    setShowCountdown(false);
    // Reset game metrics
    setScore(0);
    setCorrectResponses(0);
    setIncorrectResponses(0);
    setMisses(0);
    setReactionTimes([]);
    setGameHistory(Array(patternCount).fill(null));
    setCurrentPosition(0);
    setTimeLeft(GAME_DURATION);
    setGameStarted(true);
    isPlayingRef.current = true;

    // Generate the sequence of patterns
    const sequence = generateSequence();
    setCurrentSequence(sequence);

    // Start game timer
    timerRef.current = setInterval(() => {
      setTimeLeft((prevTime) => {
        if (prevTime <= 1) {
          clearInterval(timerRef.current);
          endGame();
          return 0;
        }
        return prevTime - 1;
      });
    }, 1000);

    // Start showing the first pattern (with a slight delay)
    setTimeout(() => {
      showNextPattern(sequence, 0);
    }, 500);

    // Update the multiplayer game state to active
    if (updateGameState) {
      updateGameState({
        status: "active",
        myProgress: 0,
        myScore: 0,
      });
    }
  };

  // --- Sequence Generation ---

  const generateSequence = useCallback(() => {
    const sequence = [];
    const matchChance = 0.3; // 30% chance for an n-back match
    for (let i = 0; i < patternCount; i++) {
      if (i < level) {
        sequence.push(Math.floor(Math.random() * numSquares));
        continue;
      }
      const shouldMatch = Math.random() < matchChance;
      if (shouldMatch) {
        sequence.push(sequence[i - level]);
      } else {
        let newPattern;
        do {
          newPattern = Math.floor(Math.random() * numSquares);
        } while (newPattern === sequence[i - level]);
        sequence.push(newPattern);
      }
    }
    return sequence;
  }, [level, numSquares]);

  // --- Pattern Display Loop ---

  const showNextPattern = (sequence, position) => {
    if (position >= sequence.length) {
      endGame();
      return;
    }

    // Show the pattern on the grid
    setActiveSquare(sequence[position]);
    lastInteractionTime.current = Date.now();

    // Hide the pattern after the visibility duration
    setTimeout(() => {
      setActiveSquare(null);

      // Allow time for player response; then move on
      setTimeout(() => {
        // For positions that require a response, check for a missed input
        if (position >= level) {
          const shouldMatch = sequence[position] === sequence[position - level];
          const didRespond = gameHistory[position] !== null;
          if (shouldMatch && !didRespond) {
            setMisses((prev) => prev + 1);
            setGameHistory((prevHistory) => {
              const newHistory = [...prevHistory];
              newHistory[position] = false;
              return newHistory;
            });
          }
        }

        setCurrentPosition(position + 1);

        // Update multiplayer game state with progress
        if (updateGameState) {
          updateGameState({
            myProgress: (position + 1) / sequence.length,
            myScore: score,
          });
        }

        // If at the end of the sequence, end the game
        if (position + 1 >= sequence.length) {
          endGame();
          return;
        }
        showNextPattern(sequence, position + 1);
      }, baseSpeed - basePatternVisibility);
    }, basePatternVisibility);
  };

  // --- User Response Handling ---

  const handleUserInput = (isMatch) => {
    if (!isPlayingRef.current || currentPosition < level) return;

    const reactionTime = Date.now() - lastInteractionTime.current;
    setReactionTimes((prev) => [...prev, reactionTime]);

    const currentPattern = currentSequence[currentPosition];
    const nBackPattern = currentSequence[currentPosition - level];
    const isCorrectMatch = currentPattern === nBackPattern;

    if ((isMatch && isCorrectMatch) || (!isMatch && !isCorrectMatch)) {
      setScore((prev) => prev + 10);
      setCorrectResponses((prev) => prev + 1);
    } else {
      setScore((prev) => Math.max(0, prev - 5));
      setIncorrectResponses((prev) => prev + 1);
    }

    // Record the player's response
    setGameHistory((prevHistory) => {
      const newHistory = [...prevHistory];
      newHistory[currentPosition] = isMatch;
      return newHistory;
    });

    // Update multiplayer state with the latest response
    if (updateGameState) {
      updateGameState({
        myProgress: currentPosition / currentSequence.length,
        myScore: score,
        myLastResponse: {
          response: isMatch,
          reactionTime,
        },
      });
    }
  };

  // --- Ending the Game ---

  const endGame = () => {
    if (!isPlayingRef.current) return;
    clearInterval(timerRef.current);
    isPlayingRef.current = false;
    setGameStarted(false);
    setGameMode("results");

    // Prepare performance stats for submission
    if (submitScore) {
      const stats = {
        correctResponses,
        incorrectResponses,
        misses,
        averageReactionTime:
          reactionTimes.length > 0
            ? Math.round(
                reactionTimes.reduce((a, b) => a + b, 0) / reactionTimes.length
              )
            : 0,
        totalPatterns: patternCount,
        gameLength: GAME_DURATION - timeLeft,
        timestamp: new Date(),
      };

      submitScore(score, stats);

      if (updateGameState) {
        updateGameState({
          myProgress: 1,
          myScore: score,
          myLastResponse: { gameComplete: true, finalStats: stats },
        });
      }
    }
  };

  // --- Utility: Format Time ---
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs < 10 ? "0" : ""}${secs}`;
  };

  // --- Render UI ---
  return (
    <div className="bg-gradient-to-br from-slate-900 to-slate-950 text-white p-4 md:p-6">
      <div>
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <button
            onClick={() => navigate("/gameroom")}
            className="flex items-center gap-2 text-slate-300 hover:text-white transition-colors"
          >
            <ArrowLeft size={20} />
            <span>Back to Gameroom</span>
          </button>
          <div className="flex items-center gap-3">
            <h1 className="text-xl md:text-2xl font-bold text-blue-300">
              Memory Matrix
            </h1>
            <div className="p-2 bg-blue-500/10 rounded-full">
              <Brain className="text-blue-400" size={20} />
            </div>
          </div>
          <button
            onClick={() => {}}
            className="p-2 bg-slate-800 rounded-lg text-slate-400 hover:text-white transition-colors"
          >
            <Info size={20} />
          </button>
        </div>

        {/* Opponent Progress */}
        {gameState && opponent && (
          <div className="mb-4 bg-slate-800/60 p-3 rounded-lg">
            <div className="flex justify-between items-center mb-1">
              <span className="text-sm text-slate-300">
                {opponent.name}'s Progress
              </span>
              <span className="text-xs text-white">
                {gameState?.progress && gameState.progress[opponent.email]
                  ? `${Math.round(gameState.progress[opponent.email] * 100)}%`
                  : "0%"}
              </span>
            </div>
            <div className="h-1.5 bg-slate-700 rounded-full overflow-hidden">
              <div
                className="bg-blue-500 h-full rounded-full"
                style={{
                  width: `${
                    ((gameState?.progress &&
                      gameState.progress[opponent.email]) ||
                      0) * 100
                  }%`,
                }}
              ></div>
            </div>
            {gameState?.scores &&
              gameState.scores[opponent.email] !== undefined && (
                <div className="mt-1 text-right text-xs text-blue-300">
                  Score: {gameState.scores[opponent.email]}
                </div>
              )}
          </div>
        )}

        {/* Overlay: Ready-to-Play */}
        {!gameStarted && (
          <div className="absolute inset-0 z-50 flex items-center justify-center bg-slate-900/80">
            {waitingForOpponent ? (
              <div className="flex flex-col items-center gap-4">
                <div className="w-16 h-16 border-4 border-t-transparent border-blue-500 rounded-full animate-spin"></div>
                <p className="text-white text-xl">Waiting for opponent...</p>
              </div>
            ) : (
              <button
                onClick={handleReadyClick}
                className="py-3 px-6 bg-gradient-to-r from-blue-500 to-cyan-600 hover:brightness-110 text-white rounded-lg"
              >
                Ready to Play
              </button>
            )}
          </div>
        )}

        {/* Countdown Overlay */}
        {showCountdown && (
          <div className="absolute inset-0 z-50 flex items-center justify-center bg-slate-900/80">
            <div className="flex flex-col items-center">
              <div className="text-6xl font-bold text-white mb-4">
                {countdown > 0 ? countdown : "GO!"}
              </div>
              <div className="text-xl text-slate-300">Get ready...</div>
            </div>
          </div>
        )}

        {/* Game Play Screen */}
        {gameMode === "play" && gameStarted && (
          <div className="p-2 md:p-4">
            <div className="flex justify-between items-center mb-6">
              <div className="bg-gradient-to-r from-blue-500/20 to-cyan-600/20 px-4 py-2 rounded-lg">
                <span className="text-blue-300 font-medium">
                  Level: {level}-Back
                </span>
              </div>
              <div className="flex items-center gap-4">
                <div className="bg-slate-700/50 px-4 py-2 rounded-lg flex items-center gap-2">
                  <Clock size={18} className="text-slate-400" />
                  <span className="text-white font-mono">
                    {formatTime(timeLeft)}
                  </span>
                </div>
                <div className="bg-gradient-to-r from-blue-500/20 to-cyan-600/20 px-4 py-2 rounded-lg">
                  <span className="text-white font-medium">Score: {score}</span>
                </div>
              </div>
            </div>

            <div className="flex flex-col md:flex-row gap-4 items-center justify-center">
              {/* Game Grid */}
              <div className="relative">
                <div className="grid grid-cols-3 gap-2 bg-slate-900/60 p-2 rounded-xl border border-slate-700">
                  {[...Array(numSquares)].map((_, index) => (
                    <div
                      key={index}
                      className={`w-16 h-16 md:w-20 md:h-20 rounded-lg border-2 transition-colors ${
                        activeSquare === index
                          ? "bg-blue-500 border-blue-400"
                          : "bg-slate-800/60 border-slate-700"
                      }`}
                    ></div>
                  ))}
                </div>
              </div>

              {/* Game Controls */}
              <div className="md:w-1/3 space-y-6">
                <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-4">
                  <h3 className="text-white font-medium mb-3">Game Progress</h3>
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-slate-400 text-sm">Position</span>
                    <span className="text-white font-medium">
                      {currentPosition + 1}/{currentSequence.length}
                    </span>
                  </div>
                  <div className="h-2 bg-slate-700 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-gradient-to-r from-blue-500 to-cyan-600"
                      style={{
                        width: `${
                          (currentPosition / currentSequence.length) * 100
                        }%`,
                      }}
                    ></div>
                  </div>
                </div>
                <div className="space-y-3">
                  {currentPosition >= level ? (
                    <>
                      <button
                        onClick={() => handleUserInput(true)}
                        className="w-full py-3 bg-green-500/20 hover:bg-green-500/30 text-green-400 font-medium rounded-lg transition-colors flex items-center justify-center gap-2"
                      >
                        <CheckCircle size={20} />
                        Match
                      </button>
                      <button
                        onClick={() => handleUserInput(false)}
                        className="w-full py-3 bg-red-500/20 hover:bg-red-500/30 text-red-400 font-medium rounded-lg transition-colors flex items-center justify-center gap-2"
                      >
                        <XCircle size={20} />
                        No Match
                      </button>
                    </>
                  ) : (
                    <div className="w-full py-4 bg-slate-700/30 text-slate-400 rounded-lg text-center">
                      <p>Remember these patterns...</p>
                      <p className="text-xs mt-1">
                        Response buttons will appear after position {level}
                      </p>
                    </div>
                  )}
                </div>
                <div>
                  <button
                    onClick={() => {
                      if (
                        confirm("Are you sure you want to end the game early?")
                      ) {
                        endGame();
                      }
                    }}
                    className="w-full py-2 bg-slate-700 hover:bg-slate-600 text-slate-300 rounded-lg transition-colors text-sm"
                  >
                    End Game
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Results Screen */}
        {gameMode === "results" && (
          <div className="p-2 md:p-4">
            <div className="text-center mb-8">
              <div className="w-20 h-20 bg-gradient-to-br from-blue-500 to-cyan-600 rounded-full mx-auto flex items-center justify-center mb-4">
                <Trophy size={40} className="text-white" />
              </div>
              <h2 className="text-2xl font-bold text-white mb-2">
                Game Complete!
              </h2>
              <p className="text-slate-300">
                You've completed the {level}-Back Memory Matrix challenge.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
              <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-5">
                <h3 className="text-lg font-medium text-white mb-4 flex items-center gap-2">
                  <Trophy size={20} className="text-blue-400" />
                  Your Results
                </h3>
                <div className="space-y-4">
                  <div className="flex justify-between items-center pb-2 border-b border-slate-700">
                    <span className="text-slate-300">Final Score</span>
                    <span className="text-2xl font-bold text-blue-300">
                      {score}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-slate-300">Level</span>
                    <span className="text-white font-medium">{level}-Back</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-slate-300">Correct Responses</span>
                    <span className="text-green-400">{correctResponses}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-slate-300">Incorrect Responses</span>
                    <span className="text-red-400">{incorrectResponses}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-slate-300">Missed Matches</span>
                    <span className="text-amber-400">{misses}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-slate-300">Avg. Reaction Time</span>
                    <span className="text-white">
                      {reactionTimes.length > 0
                        ? `${Math.round(
                            reactionTimes.reduce((a, b) => a + b, 0) /
                              reactionTimes.length
                          )} ms`
                        : "N/A"}
                    </span>
                  </div>
                </div>
              </div>

              <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-5">
                <h3 className="text-lg font-medium text-white mb-4 flex items-center gap-2">
                  <BarChart2 size={20} className="text-blue-400" />
                  Performance Analysis
                </h3>
                <div className="space-y-5">
                  <div>
                    <div className="flex justify-between items-center mb-1">
                      <span className="text-slate-300 text-sm">Accuracy</span>
                      <span className="text-white text-sm">
                        {correctResponses + incorrectResponses > 0
                          ? `${Math.round(
                              (correctResponses /
                                (correctResponses + incorrectResponses)) *
                                100
                            )}%`
                          : "N/A"}
                      </span>
                    </div>
                    <div className="h-2 bg-slate-700 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-gradient-to-r from-blue-500 to-cyan-600"
                        style={{
                          width:
                            correctResponses + incorrectResponses > 0
                              ? `${
                                  (correctResponses /
                                    (correctResponses + incorrectResponses)) *
                                  100
                                }%`
                              : "0%",
                        }}
                      ></div>
                    </div>
                  </div>

                  <div>
                    <h4 className="text-white font-medium mb-2">
                      Cognitive Benefits
                    </h4>
                    <ul className="space-y-2 text-sm">
                      <li className="flex items-start gap-2">
                        <div className="p-1 bg-blue-500/10 rounded-full mt-0.5">
                          <Brain size={14} className="text-blue-400" />
                        </div>
                        <span className="text-slate-300">
                          Working memory improvement
                        </span>
                      </li>
                      <li className="flex items-start gap-2">
                        <div className="p-1 bg-blue-500/10 rounded-full mt-0.5">
                          <Brain size={14} className="text-blue-400" />
                        </div>
                        <span className="text-slate-300">
                          Enhanced focus and attention
                        </span>
                      </li>
                      <li className="flex items-start gap-2">
                        <div className="p-1 bg-blue-500/10 rounded-full mt-0.5">
                          <Brain size={14} className="text-blue-400" />
                        </div>
                        <span className="text-slate-300">
                          Neural pathway strengthening
                        </span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex flex-col md:flex-row gap-4 justify-center">
              <button
                onClick={() => {
                  setGameMode("play");
                  startGame();
                }}
                className="py-3 px-6 bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition-colors"
              >
                Play Again
              </button>
              <button
                onClick={() => submitScore(score)}
                className="py-3 px-6 bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white rounded-lg transition-colors flex items-center justify-center gap-2 font-medium"
              >
                <Trophy size={18} />
                Return to Lobby
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MemoryMatrixMultiWrapper;
